import { AppManager } from 'core';
import { action, comparer, computed, observable, reaction } from 'mobx';
import { getProp } from './TableRow';

interface TableStoreProps {
  conn: any;
  subRowsAccessor: string;
}

interface ExpandableRowsState {
  [key: string]: boolean;
}

export class TableStore extends AppManager {
  @observable expandableRowsState: ExpandableRowsState = {};

  constructor(parent: any, props: TableStoreProps) {
    super(parent, props);
    const { conn, subRowsAccessor } = props;

    this.disposers.push(
      reaction(
        () => conn.nodes,
        nodesArray => {
          this.setupExpandableRows(nodesArray, subRowsAccessor);
        },
        {
          equals: comparer.structural,
          fireImmediately: true,
        },
      ),
    );
  }

  @action setupExpandableRows(nodes: any[], subRowsAccessor: string) {
    this.expandableRowsState = nodes.reduce((acc: any, node: any) => {
      const nodeId = node.id;
      if (nodeId && getProp(node, subRowsAccessor)?.length > 1) {
        acc[nodeId] = true;
      }
      return acc;
    }, {});
  }

  @action setRowExpanded(rowId: string, isExpanded: boolean) {
    this.expandableRowsState[rowId] = isExpanded;
  }

  @action toggleRowExpanded(rowId: string) {
    this.expandableRowsState[rowId] = !this.expandableRowsState[rowId];
  }

  @action setAllRowsExpanded(isExpanded: boolean) {
    Object.keys(this.expandableRowsState).forEach(rowId => {
      this.expandableRowsState[rowId] = isExpanded;
    });
  }

  @action collapseAll() {
    this.setAllRowsExpanded(false);
  }

  @action expandAll() {
    this.setAllRowsExpanded(true);
  }

  @computed get areAllRowsExpanded() {
    return Object.values(this.expandableRowsState).every(val => val === true);
  }

  @computed get numExpandableRows() {
    return Object.keys(this.expandableRowsState).length;
  }
}
